import React from "react";
import type { TalentListItem } from "components/Screens/Marketplace/Customer/types";

import CustomAvatar from "components/common/Avatar";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";

import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";

import PakistanFlag from "assets/svgs/PakistanFlag";
import CostaRicaFlag from "assets/svgs/CostaRicaFlag";
import PeruFlag from "assets/svgs/PeruFlag";

import "./TalentCard.scss";

const FLAG_COMPONENTS: Record<string, React.ComponentType<{ width?: string; height?: string }>> = {
  Pakistan: PakistanFlag,
  "Costa Rica": CostaRicaFlag,
  Peru: PeruFlag,
};

interface TalentCardProps {
  talent: TalentListItem;
  onClick?: () => void;
  // eslint-disable-next-line no-unused-vars
  onRequestInterview?: (_talentId: string) => void;
  isRequesting?: boolean;
}

function TalentCard({ talent, onClick, onRequestInterview, isRequesting = false }: TalentCardProps) {
  const FlagComponent = talent.country ? FLAG_COMPONENTS[talent.country] : undefined;
  const fullName = getTalentFullName(talent);

  const safeBio = typeof talent.bio === "string" ? talent.bio : "";
  const languages = Array.isArray(talent.languagesSpoken) ? talent.languagesSpoken : [];
  const yearsOfExp = talent.totalExperience ?? 0;
  const isRequested = talent.interviewRequested || false;

  const truncateBio = (bio: string, maxLength: number = 175) => {
    if (bio.length <= maxLength) return bio;
    const truncated = bio.substring(0, maxLength).trim();
    return `${truncated}...`;
  };

  const handleRequestInterview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isRequested && !isRequesting && onRequestInterview) {
      // eslint-disable-next-line no-underscore-dangle
      onRequestInterview(talent._id);
    }
  };

  return (
    <div className="talent-card" onClick={onClick} role="button" tabIndex={0}>
      <div className="talent-card__header">
        {talent.profilePicture ? (
          <img src={talent.profilePicture} alt={fullName} className="talent-card__avatar" />
        ) : (
          <CustomAvatar
            size="xl"
            sx={{ width: 72, height: 72 }}
            src={talent.profilePicture}
            className="talent-card__avatar"
            backgroundColor="var(--color-primitives-neutral-300) !important"
          />
        )}

        <div className="talent-card__info">
          <div className="talent-card__name-row">
            <div className="talent-card__name">
              {fullName}
              {FlagComponent && (
                <span className="talent-card__flag">
                  <FlagComponent width="12" height="12" />
                </span>
              )}
            </div>
          </div>

          <div className="talent-card__meta-row">
            <div className="talent-card__meta-item">
              <WorkOutlineOutlinedIcon sx={{ fontSize: 16, color: "#7B7B7B" }} />
              <span>{yearsOfExp}+ years of experience</span>
            </div>
            <div className="talent-card__meta-divider" aria-hidden />
            <div className="talent-card__meta-item">
              <TranslateOutlinedIcon sx={{ fontSize: 16, color: "#7B7B7B" }} />
              <span>Speaks {languages.length ? languages.join(", ") : "-"}</span>
            </div>
          </div>

          <div className="talent-card__bio">
            {truncateBio(safeBio)}
            {safeBio.length > 175 && (
              <button
                type="button"
                className="talent-card__learn-more"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) onClick();
                }}
              >
                Learn more
              </button>
            )}
          </div>

          <div className="talent-card__actions">
            <button
              type="button"
              className="talent-card__btn talent-card__btn--primary"
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick();
              }}
            >
              View full profile
            </button>
            {isRequested ? (
              <button type="button" className="talent-card__btn talent-card__btn--disabled" disabled>
                <CheckIcon sx={{ fontSize: 16, color: "#888888" }} />
                Interview requested
              </button>
            ) : (
              <button
                type="button"
                className="talent-card__btn talent-card__btn--outlined"
                onClick={handleRequestInterview}
                disabled={isRequesting}
              >
                {isRequesting ? (
                  <CircularProgress size={16} sx={{ color: "#8139d0" }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#8139D0" strokeWidth="1.5" />
                    <circle cx="12" cy="9.5" r="3" stroke="#8139D0" strokeWidth="1.5" />
                    <path
                      d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                      stroke="#8139D0"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                Request interview
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TalentCard;
